import type { MatchEvent } from '@stanwars/sim';

export type SpriteState = 'idle' | 'attack' | 'hurt' | 'ko';
export type UnitKey = `${'A' | 'B'}${0 | 1 | 2 | 3 | 4}`;

export interface DamageNumber { unit: UnitKey; amount: number; isCrit: boolean; ageFrames: number; }
export interface VfxSpawn { unit: UnitKey; kind: string; ageFrames: number; }

export interface Frame {
  spriteStates: Map<UnitKey, SpriteState>;
  damageNumbers: DamageNumber[];
  vfxSpawns: VfxSpawn[];
  hpA: readonly number[]; // current HP for side A slots
  hpB: readonly number[]; // current HP for side B slots
}

export interface Playback {
  totalFrames: number;
  fps: number;
  frames: Frame[];
  maxHpA: readonly number[];
  maxHpB: readonly number[];
}

const ANIM_FRAMES = {
  attack: 30,
  hurt: 18,
};

const DAMAGE_NUM_LIFE_FRAMES = 30;
const VFX_LIFE_FRAMES = 24;

function unitKey(side: 'A' | 'B', slot: number): UnitKey {
  return `${side}${slot}` as UnitKey;
}

const ALL_UNITS: UnitKey[] = [];
for (const side of ['A', 'B'] as const) for (let s = 0; s < 5; s++) ALL_UNITS.push(unitKey(side, s));

export function buildPlayback(
  events: MatchEvent[],
  fps = 30,
  startHpA: readonly number[] = [100, 100, 100, 100, 100],
  startHpB: readonly number[] = [100, 100, 100, 100, 100],
): Playback {
  const lastTick = events.length === 0 ? 0 : events[events.length - 1]!.t;
  const totalFrames = Math.max(fps, (lastTick + 1) * fps);

  // Pre-compute HP at each tick from events
  const hpByTickA: number[][] = [];
  const hpByTickB: number[][] = [];
  const curHpA = [...startHpA];
  const curHpB = [...startHpB];

  const sortedEvents = [...events].sort((a, b) => a.t - b.t);
  let evIdx = 0;
  const totalTicks = lastTick + 2;
  for (let tick = 0; tick < totalTicks; tick++) {
    while (evIdx < sortedEvents.length && sortedEvents[evIdx]!.t === tick) {
      const ev = sortedEvents[evIdx]!;
      if (ev.kind === 'damage') {
        if (ev.side === 'A') curHpA[ev.slot] = Math.max(0, (curHpA[ev.slot] ?? 0) - ev.amount);
        else curHpB[ev.slot] = Math.max(0, (curHpB[ev.slot] ?? 0) - ev.amount);
      } else if (ev.kind === 'heal') {
        if (ev.side === 'A') curHpA[ev.slot] = Math.min(startHpA[ev.slot] ?? 100, (curHpA[ev.slot] ?? 0) + ev.amount);
        else curHpB[ev.slot] = Math.min(startHpB[ev.slot] ?? 100, (curHpB[ev.slot] ?? 0) + ev.amount);
      } else if (ev.kind === 'ko') {
        if (ev.side === 'A') curHpA[ev.slot] = 0;
        else curHpB[ev.slot] = 0;
      }
      evIdx++;
    }
    hpByTickA[tick] = [...curHpA];
    hpByTickB[tick] = [...curHpB];
  }

  const frames: Frame[] = Array.from({ length: totalFrames }, (_, f) => {
    const tick = Math.floor(f / fps);
    const tickCapped = Math.min(tick, totalTicks - 1);
    return {
      spriteStates: new Map<UnitKey, SpriteState>(),
      damageNumbers: [],
      vfxSpawns: [],
      hpA: hpByTickA[tickCapped] ?? [...startHpA],
      hpB: hpByTickB[tickCapped] ?? [...startHpB],
    };
  });

  // Group events by starting frame
  const evByFrame = new Map<number, MatchEvent[]>();
  for (const ev of sortedEvents) {
    const f = ev.t * fps;
    const list = evByFrame.get(f) ?? [];
    list.push(ev);
    evByFrame.set(f, list);
  }

  // Scan frame-by-frame, maintaining live state per unit
  const liveState = new Map<UnitKey, { state: SpriteState; endFrame: number }>();
  for (const u of ALL_UNITS) liveState.set(u, { state: 'idle', endFrame: totalFrames });

  for (let f = 0; f < totalFrames; f++) {
    const evs = evByFrame.get(f) ?? [];
    for (const ev of evs) {
      let unit: UnitKey | null = null;
      let state: SpriteState | null = null;
      let dur = 0;
      if (ev.kind === 'skill_fire') {
        unit = unitKey(ev.side, ev.sourceSlot); state = 'attack'; dur = ANIM_FRAMES.attack;
      } else if (ev.kind === 'damage') {
        unit = unitKey(ev.side, ev.slot); state = 'hurt'; dur = ANIM_FRAMES.hurt;
        const cur = liveState.get(unit);
        if (cur && cur.state === 'ko') { unit = null; state = null; }
      } else if (ev.kind === 'ko') {
        unit = unitKey(ev.side, ev.slot); state = 'ko'; dur = totalFrames - f;
      }
      if (unit && state !== null) {
        liveState.set(unit, { state, endFrame: f + dur });
      }
    }
    for (const u of ALL_UNITS) {
      const s = liveState.get(u)!;
      if (s.state !== 'ko' && f >= s.endFrame) {
        liveState.set(u, { state: 'idle', endFrame: totalFrames });
      }
      frames[f]!.spriteStates.set(u, s.state);
    }
  }

  // Emit damage numbers and VFX spawns spanning their lifetimes
  for (const ev of sortedEvents) {
    const startFrame = ev.t * fps;
    if (ev.kind === 'damage') {
      const unit = unitKey(ev.side, ev.slot);
      for (let i = 0; i < DAMAGE_NUM_LIFE_FRAMES && startFrame + i < totalFrames; i++) {
        frames[startFrame + i]!.damageNumbers.push({
          unit, amount: ev.amount, isCrit: ev.isCrit, ageFrames: i,
        });
      }
    } else if (ev.kind === 'skill_fire') {
      const unit = unitKey(ev.side, ev.sourceSlot);
      for (let i = 0; i < VFX_LIFE_FRAMES && startFrame + i < totalFrames; i++) {
        frames[startFrame + i]!.vfxSpawns.push({ unit, kind: 'slash', ageFrames: i });
      }
    }
  }

  return { totalFrames, fps, frames, maxHpA: startHpA, maxHpB: startHpB };
}
